import type { QueryBuilderSubquery } from '@ardoq/api-types';
import { isEqual } from 'lodash';
import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { filterRouteActive } from 'router/declarative/filterRouteActive';
import { type AppLocation, location$ } from 'router/declarative/location$';
import {
  getHiddenParam,
  getSearchParam,
} from 'router/declarative/locationParams';
import { INVENTORY_ID_SEPARATOR, InventoryModule } from './consts';
import type {
  DataOverviewInventoryNavigationState,
  InventoryNavigationState,
} from './inventoryNavigationTypes';

export const INVENTORY_ROUTE = '/inventory';

export type InventoryLocation = AppLocation<typeof INVENTORY_ROUTE>;

export const defaultInventoryNavigationState: DataOverviewInventoryNavigationState =
  {
    inventoryModule: InventoryModule.OVERVIEW,
    dataSourceType: 'none',
    useCase: 'pageManageData',
  };

const toFilterSubquery = (
  location: InventoryLocation
): QueryBuilderSubquery | null => {
  const filterSubquery = getSearchParam(location, 'inventoryFilters');
  if (!filterSubquery || filterSubquery.rules.length === 0) return null;
  return filterSubquery;
};

const toNavigationStateFromPath = (
  location: InventoryLocation
): InventoryNavigationState => {
  switch (location.route) {
    case '/inventory/browse/workspace/:workspaceIds':
      return {
        inventoryModule: InventoryModule.BROWSE,
        dataSourceType: 'workspace',
        useCase: 'pageManageData',
        selectedWorkspaceIds: location.params.workspaceIds,
        filterSubquery: toFilterSubquery(location),
      };
    case '/inventory/browse/component-type/:componentTypeIds':
      return {
        inventoryModule: InventoryModule.BROWSE,
        dataSourceType: 'componentType',
        useCase: 'pageManageData',
        componentTypeIds: location.params.componentTypeIds,
        filterSubquery: toFilterSubquery(location),
      };
    /**
     * Currently not supported due to the frontend-defined filters from report
     * and limitations of how we display advanced-search nested filters in Inventory.
     * We will go back to it once we can fully render Reports in Inventory.
     */
    case '/inventory/browse/report/:reportId':
      return {
        inventoryModule: InventoryModule.BROWSE,
        dataSourceType: 'report',
        useCase: 'pageManageData',
        selectedReportId: location.params.reportId,
        selectedReportFilters: null,
      };
    case '/inventory/browse/my-applications':
      return {
        inventoryModule: InventoryModule.BROWSE,
        dataSourceType: 'myApplicationType',
        useCase: 'pageManageData',
      };
    case '/inventory/browse/datasets':
      return {
        inventoryModule: InventoryModule.BROWSE,
        dataSourceType: 'dataset',
        useCase: 'pageManageData',
        loadedStateParams: [],
      };
    default:
      return defaultInventoryNavigationState;
  }
};

/**
 * The history state outlives deploys, so an entry written by an older version
 * of `InventoryNavigationState` can be missing fields the type promises.
 */
const hasDatasourceFields = (state: InventoryNavigationState): boolean => {
  switch (state.dataSourceType) {
    case 'workspace':
      return Array.isArray(state.selectedWorkspaceIds);
    case 'componentType':
      return Array.isArray(state.componentTypeIds);
    case 'report':
      return typeof state.selectedReportId === 'string';
    case 'dataset':
      return Array.isArray(state.loadedStateParams);
    case 'myApplicationType':
    case 'none':
      return true;
    default:
      state satisfies never;
      return false;
  }
};

/**
 * The history state is only trusted when it describes the same datasource as
 * the path, so a hand-edited URL cannot resurrect an unrelated selection.
 */
const toInventoryNavigationState = (
  location: InventoryLocation
): InventoryNavigationState => {
  const stateFromPath = toNavigationStateFromPath(location);
  const stateFromHistory = getHiddenParam(location, 'inventoryViewState');
  if (
    stateFromHistory?.dataSourceType !== stateFromPath.dataSourceType ||
    !hasDatasourceFields(stateFromHistory)
  ) {
    return stateFromPath;
  }
  return stateFromHistory;
};

const toFilterSearchParams = (
  filterSubquery: QueryBuilderSubquery | null
): { inventoryFilters: QueryBuilderSubquery } | undefined => {
  if (!filterSubquery || filterSubquery.rules.length === 0) return undefined;
  return { inventoryFilters: filterSubquery };
};

export const toInventoryLocation = (
  state: InventoryNavigationState
): InventoryLocation => {
  const hiddenParams = { inventoryViewState: state };
  switch (state.dataSourceType) {
    case 'workspace':
      return {
        route: '/inventory/browse/workspace/:workspaceIds',
        params: { workspaceIds: state.selectedWorkspaceIds },
        searchParams: toFilterSearchParams(state.filterSubquery),
        hiddenParams,
      };
    case 'componentType':
      return {
        route: '/inventory/browse/component-type/:componentTypeIds',
        params: { componentTypeIds: state.componentTypeIds },
        searchParams: toFilterSearchParams(state.filterSubquery),
        hiddenParams,
      };
    case 'report':
      return {
        route: '/inventory/browse/report/:reportId',
        params: { reportId: state.selectedReportId },
        hiddenParams,
      };
    case 'myApplicationType':
      return {
        route: '/inventory/browse/my-applications',
        params: {},
        hiddenParams,
      };
    case 'dataset':
      return {
        route: '/inventory/browse/datasets',
        params: {},
        hiddenParams,
      };
    case 'none':
      return { route: '/inventory/overview', params: {}, hiddenParams };
    default:
      state satisfies never;
      return { route: '/inventory/overview', params: {}, hiddenParams };
  }
};

const toDatasourceKey = (state: InventoryNavigationState): string => {
  switch (state.dataSourceType) {
    case 'workspace':
      return `workspace/${state.selectedWorkspaceIds.join(INVENTORY_ID_SEPARATOR)}`;
    case 'componentType':
      return `componentType/${state.componentTypeIds.join(INVENTORY_ID_SEPARATOR)}/${(state.selectedWorkspaceIds ?? []).join(INVENTORY_ID_SEPARATOR)}`;
    case 'report':
      return `report/${state.selectedReportId}`;
    case 'myApplicationType':
      return 'myApplicationType';
    case 'dataset':
      return `dataset/${JSON.stringify(state.loadedStateParams)}`;
    case 'none':
      return 'none';
    default:
      state satisfies never;
      return 'none';
  }
};

export const inventoryRouteNavigation$: Observable<InventoryNavigationState> =
  location$.pipe(
    filterRouteActive(INVENTORY_ROUTE),
    map(toInventoryNavigationState),
    distinctUntilChanged(isEqual)
  );

/**
 * Emits only when the inventory is pointed at a different datasource, so
 * consumers that reset per-datasource state are not disturbed by filter
 * changes, which navigate within the same datasource.
 */
export const inventoryDatasourceNavigation$: Observable<InventoryNavigationState> =
  inventoryRouteNavigation$.pipe(
    distinctUntilChanged(
      (previous, current) =>
        toDatasourceKey(previous) === toDatasourceKey(current)
    )
  );
